// Share Capital verification (legacy /AccountsApproval/VerifyShareCapital) — newly added authorised /
// issued / paid-up capital, shown next to the company's existing approved capital, with the uploaded
// supporting document. Verified/approved via the shared status list (legacy drops "Return" here).
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Briefcase, Clock, ExternalLink } from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';
import { buildCapitalShareUrl } from '../../config/s3Config';

import {
    fetchShareCapitalQueue,
    fetchShareCapitalDetail,
    fetchCapitalDetails,
    submitApproveShareCapital,
    clearShareCapitalDetail,
    resetVerifyShareCapital,
    selectVSCQueue, selectVSCQueueLoading, selectVSCQueueError,
    selectVSCDetail, selectVSCDetailLoading, selectVSCDetailError,
    selectVSCCapital, selectVSCCapitalLoading, selectVSCVerifyLoading,
} from '../../slices/accountsSlice/verifyShareCapitalSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID,
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

const ACTION_VERB = {
    Verify: 'Verified Successfully',
    Approve: 'Approved Successfully',
    Reject: 'Rejected Successfully',
};

const StatusBadge = () => (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 whitespace-nowrap">Verification Pending</span>
);

const formatAmount = (v) => (v != null && v !== '' ? `₹ ${formatIndianCurrency(v)}` : '—');

const thCls = 'px-4 py-2 text-[10px] font-bold text-gray-500 uppercase';
const tdCls = 'px-4 py-2 text-gray-700 dark:text-gray-300';

const VerifyShareCapital = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;

    const queue = useSelector(selectVSCQueue);
    const queueLoading = useSelector(selectVSCQueueLoading);
    const queueError = useSelector(selectVSCQueueError);
    const detail = useSelector(selectVSCDetail);
    const detailLoading = useSelector(selectVSCDetailLoading);
    const detailError = useSelector(selectVSCDetailError);
    const capital = useSelector(selectVSCCapital);
    const capitalLoading = useSelector(selectVSCCapitalLoading);
    const verifyLoading = useSelector(selectVSCVerifyLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const remarks = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const [selectedItem, setSelectedItem] = useState(null);
    const [comment, setComment] = useState('');
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);

    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';

    // Only trust the detail that belongs to the row currently selected.
    const share = selectedItem && String(detail?.Refno) === String(selectedItem.Refno) ? detail : null;

    useEffect(() => {
        if (roleId) dispatch(fetchShareCapitalQueue(roleId));
        dispatch(fetchCapitalDetails());
    }, [roleId, dispatch]);

    useEffect(() => {
        // Legacy removes "Return" from this page's status dropdown.
        dispatch(setShowReturnButton('No'));
        return () => {
            dispatch(resetVerifyShareCapital());
            dispatch(resetApprovalData());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!selectedItem) return;
        dispatch(clearShareCapitalDetail());
        dispatch(resetApprovalData());
        dispatch(setShowReturnButton('No'));
        dispatch(fetchShareCapitalDetail(selectedItem.Refno));
        setComment('');
        setShowRemarksHistory(false);
        setIsLeftPanelCollapsed(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    // MOID comes with the detail. Remarks are keyed on the transaction ref no.
    useEffect(() => {
        if (!share?.MOID || !roleId) return;
        dispatch(fetchStatusList({ MOID: share.MOID, ROID: roleId, ChkAmt: 0 }));
        dispatch(setSelectedMOID(share.MOID));
        dispatch(fetchRemarks({ trno: share.Refno, moid: share.MOID }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [share?.MOID, share?.Refno]);

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleRefresh = () => {
        if (roleId) dispatch(fetchShareCapitalQueue(roleId));
    };

    const finishAndRefresh = () => {
        setTimeout(() => {
            dispatch(fetchShareCapitalQueue(roleId));
            dispatch(fetchCapitalDetails());
            setSelectedItem(null);
            setComment('');
            setShowRemarksHistory(false);
            setIsLeftPanelCollapsed(false);
            dispatch(clearShareCapitalDetail());
            dispatch(resetApprovalData());
        }, 800);
    };

    const handleActionClick = async (action) => {
        if (!share) { toast.error('No share capital selected'); return; }
        if (!comment.trim()) { toast.error('Enter Note'); return; }

        const actionValue = action.type;
        // spApproveShareCapital reads the action from Status and the note from Remarks.
        const payload = {
            Refno: share.Refno,
            Status: actionValue,
            Remarks: comment.trim(),
            RoleId: roleId,
            CreatedBy: getCurrentUser(),
        };

        try {
            const result = await dispatch(submitApproveShareCapital(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status === 'Submitted' || status === 'Submited') {
                toast.success(`Share Capital ${ACTION_VERB[actionValue] || `${actionValue}d`}`);
                finishAndRefresh();
                return;
            }
            toast.error(status || 'Error Occurred While Verification');
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred While Verification');
        }
    };

    const handleViewDocument = () => {
        const url = buildCapitalShareUrl(share?.FileName);
        if (!url) { toast.error('No uploaded document available'); return; }
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return String(item.Refno ?? '').toLowerCase().includes(q) || item.Date?.toLowerCase().includes(q);
    });

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">Transaction {item.Refno}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.Date}</p>
                </div>
            </div>
            <div className="flex justify-end"><StatusBadge /></div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">{item.Refno}</span>
            <span className="text-gray-500 dark:text-gray-400 min-w-[100px]">{item.Date}</span>
            <span className="ml-auto"><StatusBadge /></span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
            <Briefcase className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderNewCapital = () => {
        const rows = [
            ['Authorised Capital', share.AccountCapitalNoofShare, share.AccountCapitalEachSharePrice, share.AccountCapitalAmount],
            ['Issued Capital', share.IssuedCapitalNoofShare, share.IssuedCapitalEachSharePrice, share.IssuedCapitalAmount],
            ['Paid Up Capital', share.PaidCapitalNoofShare, share.PaidCapitalEachSharePrice, share.PaidCapitalAmount],
        ];
        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700 space-y-3">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">New Added Share Capital</p>
                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-100 dark:bg-gray-800">
                                <th className={`${thCls} text-left`}>Capital</th>
                                <th className={`${thCls} text-right`}>No of Shares</th>
                                <th className={`${thCls} text-right`}>Price of Each Share</th>
                                <th className={`${thCls} text-right`}>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(([label, shares, price, amount]) => (
                                <tr key={label} className="border-t border-gray-100 dark:border-gray-700">
                                    <td className={`${tdCls} font-semibold`}>{label}</td>
                                    <td className={`${tdCls} text-right`}>{shares ?? '—'}</td>
                                    <td className={`${tdCls} text-right`}>{formatAmount(price)}</td>
                                    <td className={`${tdCls} text-right font-semibold`}>{formatAmount(amount)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {share.FileName && (
                    <button type="button" onClick={handleViewDocument}
                        className="inline-flex items-center gap-1.5 text-sm font-bold text-red-800 dark:text-red-300 underline hover:no-underline">
                        <ExternalLink className="w-4 h-4" /> View Uploaded Docs
                    </button>
                )}
            </div>
        );
    };

    const renderExistingCapital = () => (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700 space-y-3">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Existing Approved Capital</p>
            {capitalLoading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
            ) : capital.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No Data Found</p>
            ) : (
                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-100 dark:bg-gray-800">
                                <th className={`${thCls} text-left`}>Capital Type</th>
                                <th className={`${thCls} text-right`}>No of Shares</th>
                                <th className={`${thCls} text-right`}>Each Share</th>
                                <th className={`${thCls} text-right`}>Amount</th>
                                <th className={`${thCls} text-center`}>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {capital.map((c, i) => (
                                <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                                    <td className={tdCls}>{c.CapitalType}</td>
                                    <td className={`${tdCls} text-right`}>{c.shares}</td>
                                    <td className={`${tdCls} text-right`}>{formatAmount(c.Eachshare)}</td>
                                    <td className={`${tdCls} text-right`}>{formatAmount(c.shareAmount)}</td>
                                    <td className={`${tdCls} text-center`}>{c.ShareCapitalDate}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );

    const renderActions = () => {
        if (statusLoading) {
            return (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        <span className="text-gray-600 dark:text-gray-400">Loading actions…</span>
                    </div>
                </div>
            );
        }
        if (statusError) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                </div>
            );
        }
        if (!hasActions || !enabledActions || enabledActions.length === 0) {
            return (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                    <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ No actions available for this share capital</p>
                </div>
            );
        }
        return (
            <ActionButtons
                actions={enabledActions}
                onActionClick={handleActionClick}
                loading={verifyLoading}
                isVerified={true}
                comment={comment}
                showValidation={true}
            />
        );
    };

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        if (detailLoading || (!share && !detailError)) {
            return (
                <div className="flex items-center justify-center gap-3 py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading share capital details…</span>
                </div>
            );
        }
        if (detailError || !share) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ {detailError || 'Share capital details could not be loaded — it may no longer be pending at your level.'}</p>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Share Capital Updation</h2>
                    <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">Transaction {share.Refno}</p>
                    <div className="flex flex-wrap gap-2">
                        {selectedItem.Date && (
                            <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">Date: {selectedItem.Date}</span>
                        )}
                        <StatusBadge />
                    </div>
                </div>

                {renderNewCapital()}
                {renderExistingCapital()}

                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                    remarks={remarks}
                    loading={remarksLoading}
                    title="Approval History"
                />

                <SimpleCommentInput
                    comment={comment}
                    onCommentChange={(e) => setComment(e.target.value)}
                    label="Notes"
                    placeholder="Enter verification notes…"
                    required
                    rows={3}
                    showCharCount
                />
                {renderActions()}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'Share Capital Verification'} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={Briefcase}
                badgeText="Share Capital Verification"
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by transaction no, date…',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue,
                    selectedItem,
                    onItemSelect: setSelectedItem,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: 'No share capital updations pending verification!',
                        itemKey: 'Refno',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: 'Share Capital Verification',
                        icon: Briefcase,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Share Capital Details',
                        icon: Briefcase,
                        selectedTitle: 'Share Capital Verification',
                        emptyTitle: 'No Share Capital Selected',
                        emptyMessage: 'Select a share capital updation from the list to view details and take action.',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyShareCapital;
